

export class Date extends globalThis.Date
{
    protected constructor()
    {
        super();
    }
}


export namespace Date
{
    export function isDate(value: any): value is globalThis.Date
    {
        return value instanceof globalThis.Date;
    }

    export function isValidDate(date: any): date is globalThis.Date
    {
        return (
            date instanceof globalThis.Date &&
            !isNaN(date.getTime())
        );
    }

    export function areEqual(first: globalThis.Date, second: globalThis.Date): boolean
    {
        return (first.valueOf() == second.valueOf());
    }

    export function addDays(date: globalThis.Date, days: number): globalThis.Date
    {
        return new globalThis.Date(date.valueOf() + days * OneDay);
    }

    export function addHours(date: globalThis.Date, hours: number): globalThis.Date
    {
        return new globalThis.Date(date.valueOf() + hours * OneHour);
    }

    export function addMinutes(date: globalThis.Date, minutes: number): globalThis.Date
    {
        return new globalThis.Date(date.valueOf() + minutes * OneMinute);
    }

    export function addMonths(date: globalThis.Date, months: number): globalThis.Date
    {
        const year = date.getFullYear();
        const month = date.getMonth() + months;
        let day = date.getDate();

        day =
            (day > 28) ?
                Math.min(
                    day,
                    Date.from(year, month, 0).getDate()
                ) :
                day;

        return new globalThis.Date(
            year,
            month,
            day,
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds());
    }

    export function addSeconds(date: globalThis.Date, seconds: number): globalThis.Date
    {
        return new globalThis.Date(date.valueOf() + seconds * OneSecond);
    }

    export function addYears(date: globalThis.Date, years: number): globalThis.Date
    {
        return new globalThis.Date(
            date.getFullYear() + years,
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds());
    }

    export function current(): globalThis.Date
    {
        return new globalThis.Date();
    }

    export function currentYear(): globalThis.Date
    {
        const now = new globalThis.Date();
        return new globalThis.Date(now.getFullYear(), 0, 1);
    }

    export function currentMonth(): globalThis.Date
    {
        const now = new globalThis.Date();
        return new globalThis.Date(now.getFullYear(), now.getMonth(), 1);
    }

    export function today(): globalThis.Date
    {
        const now = new globalThis.Date();
        return new globalThis.Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    export function from(): globalThis.Date;
    export function from(value: string | number | globalThis.Date): globalThis.Date;
    export function from(year: number, monthIndex: number, date?: number, hours?: number, minutes?: number, seconds?: number, ms?: number): globalThis.Date;
    export function from(...args: any[]): globalThis.Date
    {
        const constructor = globalThis.Date.bind(null, ...args) as DateConstructor;
        const date = new constructor();
        return date;
    }

    export function getDate(): globalThis.Date;
    export function getDate(dateTime: globalThis.Date): globalThis.Date;
    export function getDate(dateTime: globalThis.Date, local: boolean): globalThis.Date;
    export function getDate(dateTime?: globalThis.Date, local?: boolean): globalThis.Date
    {
        dateTime = dateTime ?? new globalThis.Date();

        if (local)
        {
            const offset = dateTime.getTimezoneOffset() * OneMinute;
            dateTime = new globalThis.Date(dateTime.valueOf() + offset);
        }

        return new globalThis.Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
    }

    export function getTime(dateTime?: globalThis.Date): number
    {
        dateTime = dateTime ?? new globalThis.Date();
        const date = getDate(dateTime);
        return dateTime.valueOf() - date.valueOf();
    }

    export function format(date: globalThis.Date, format: string, utc?: boolean): string
    {
        if (!date) return "";

        const values: Record<string, () => string> =
        {
            year: () => (utc ? date.getUTCFullYear() : date.getFullYear()).toString().padStart(4, '0'),
            month: () => ((utc ? date.getUTCMonth() : date.getMonth()) + 1).toString().padStart(2, '0'),
            longMonth: () => longMonths[(utc ? date.getUTCMonth() : date.getMonth())],
            shortMonth: () => shortMonths[(utc ? date.getUTCMonth() : date.getMonth())],
            day: () => (utc ? date.getUTCDate() : date.getDate()).toString().padStart(2, '0'),
            hours: () => (utc ? date.getUTCHours() : date.getHours()).toString().padStart(2, '0'),
            minutes: () => (utc ? date.getUTCMinutes() : date.getMinutes()).toString().padStart(2, '0'),
            seconds: () => (utc ? date.getUTCSeconds() : date.getSeconds()).toString().padStart(2, '0'),
            weekDay: () => daysOfWeek[(utc ? date.getUTCDay() : date.getDay())],
            shortWeekDay: () => shortWeekDays[(utc ? date.getUTCDay() : date.getDay())]
        };

        return format.replace(/{([^\}]*)}/g, function (match, token) { return values[token]?.(); });
    }

    // TODO: just scanning a structure for dates is dangerous. Date normalization rules are about always use-case specific.
    /** @deprecated */
    export function normalizeLocalDates(object: any)
    {
        for (const key in object)
        {
            if (object[key] instanceof globalThis.Date)
                object[key] = getDate(object[key], true);
        }

        return object;
    }

    export const OneMillisecond = 1;
    export const OneSecond = 1000 * OneMillisecond;
    export const OneMinute = 60 * OneSecond;
    export const OneHour = 60 * OneMinute;
    export const OneDay = 24 * OneHour;
    export const OneWeek = 7 * OneDay;
    export const maxDate = from(8640000000000);
    export const minDate = from(-62135596800);

    export const longMonths: string[] = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    export const shortMonths: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    export const daysOfWeek: string[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    export const shortWeekDays: string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // // Migrated to @dockspace/base/common/dates/date (eg. Date.format(date, Date.format.MDY))
    // export namespace Formats
    // {
    //     export const monthDayYear = "{month}/{day}/{year}" as const;
    // }
}